// src/features/auth/components/PostLoginAgreementModal.jsx
import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { FONT_WEIGHT } from '@/core/platform/platformStandards';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Backdrop,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import GavelIcon from '@mui/icons-material/Gavel';
import { useAuth } from '@/core/contexts/AuthContext';
import agreementService from '@/services/agreementService';
import authService, { isAccreditedDeclarationAccepted } from '@/services/authService';
import lookupService from '@/services/lookupService';
import entityService from '@/services/entityService';
import ESignatureModal from '@/features/auth/registration/components/ESignatureModal';
import AccreditedInvestorDeclarationModal from '@/features/auth/registration/components/AccreditedInvestorDeclarationModal';
import QualificationCategoriesStep from '@/features/auth/registration/components/investor/QualificationCategoriesStep';
import {
  mapInvestorSeedFromApiUser,
  mapInvestorFormToEntityUpdate,
} from '@/features/auth/registration/utils/investorInstitutionMappers';
import PostLoginBillingModal from '@/features/auth/components/PostLoginBillingModal';
import ViewTermsModal from '@/features/auth/components/ViewTermsModal';
import ViewRevShareModal from '@/features/auth/components/ViewRevShareModal';
import billingService from '@/services/billingService';
import { TOUCH, ZINDEX } from '@/core/platform/platformStandards';
import { buildFirmData } from '@/utils/revShareTemplate';
import useManagerBillingSignatures from '@/features/auth/hooks/useManagerBillingSignatures';

const qualificationsValidationSchema = Yup.object({
  qualificationCategories: Yup.array()
    .of(Yup.string())
    .min(1, 'Select at least one qualification category'),
});

const PostLoginAgreementModal = ({ open, onComplete }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, updateUserAgreementStatus, refreshUserData, logout } = useAuth();

  const { selectRevShareBilling, recordSubscriptionAndCheckout } = useManagerBillingSignatures();

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsContentHash, setTermsContentHash] = useState('');
  const [termsVersion, setTermsVersion] = useState('1.0');
  const [showTermsViewModal, setShowTermsViewModal] = useState(false);
  const [showRevShareViewModal, setShowRevShareViewModal] = useState(false);
  const [showRevShareModal, setShowRevShareModal] = useState(false);
  const [showAccreditedModal, setShowAccreditedModal] = useState(false);
  const [showQualificationsModal, setShowQualificationsModal] = useState(false);
  const [institutionEntityData, setInstitutionEntityData] = useState(null);
  const [agreementData, setAgreementData] = useState({});
  const [showSubscriptionSignModal, setShowSubscriptionSignModal] = useState(false);
  const [pendingSubscriptionPlan, setPendingSubscriptionPlan] = useState('annual');
  const [pendingSubscriptionBillingAgreement, setPendingSubscriptionBillingAgreement] = useState(null);
  const [jobTitles, setJobTitles] = useState([]);
  const [institutionQualificationCategories, setInstitutionQualificationCategories] = useState([]);
  const [loadingJobTitles, setLoadingJobTitles] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stripeRedirecting, setStripeRedirecting] = useState(false);
  const [billingCompleting, setBillingCompleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const billingPickerVisible =
    open &&
    !showRevShareModal &&
    !showSubscriptionSignModal &&
    !showAccreditedModal &&
    !showQualificationsModal &&
    !stripeRedirecting &&
    !billingCompleting;

  const userRole = user?.role || user?.user_type;
  const isManager = userRole === 'manager';
  const isInvestor = userRole === 'investor';
  const hasAcceptedTerms = user?.agreement_accepted === true;
  const hasAcceptedDeclaration = isAccreditedDeclarationAccepted(user);
  const investorNeedsDeclaration = isInvestor && !hasAcceptedDeclaration;
  const investorTcOnly = isInvestor && hasAcceptedDeclaration && !hasAcceptedTerms;
  /** T&C-only when billing selection is not required (deferred default, or offline rev-share). */
  const managerTcOnly =
    isManager && user?.requires_billing_selection !== true;

  // Resolve lookup display values synchronously from the already-cached lookups.
  // lookupService.getDisplayValue reads from its in-memory cache — no async call needed.
  const firmCountryDisplay = isManager
    ? (lookupService.getDisplayValue('countries', user?.firm?.firm_country) || user?.firm?.firm_country || '')
    : '';
  const jobTitleDisplay = isManager
    ? (lookupService.getDisplayValue('job_titles', user?.manager_position) || user?.manager_position || '')
    : '';

  // Firm data for pre-filling rev-share agreement placeholders (country already resolved)
  const firmData = useMemo(
    () => (isManager ? buildFirmData(user, firmCountryDisplay) : null),
    [isManager, user, firmCountryDisplay]
  );

  // Signer details pre-filled from the user record — memoized so sign modal PDF does not reload on parent render.
  const userData = useMemo(
    () =>
      isManager
        ? {
            fullName: [user?.first_name, user?.last_name].filter(Boolean).join(' '),
            jobTitle: jobTitleDisplay,
          }
        : null,
    [isManager, user?.first_name, user?.last_name, jobTitleDisplay]
  );

  React.useEffect(() => {
    if (open && userRole === 'admin') {
      onComplete();
    }
  }, [open, userRole, onComplete]);

  const loadInvestorInstitutionContext = useCallback(async () => {
    const userId = user?.user_id || user?.userId;
    if (!userId) {
      setInstitutionQualificationCategories([]);
      setInstitutionEntityData(null);
      return [];
    }
    try {
      const response = await entityService.getEntityByInvestorId(userId);
      const entity = response?.entity || null;
      setInstitutionEntityData(entity);
      const categories = Array.isArray(entity?.qualification_categories)
        ? entity.qualification_categories
        : [];
      setInstitutionQualificationCategories(categories);
      return categories;
    } catch (entityErr) {
      console.warn('Could not load institution qualifications:', entityErr);
      setInstitutionQualificationCategories([]);
      setInstitutionEntityData(null);
      return [];
    }
  }, [user?.user_id, user?.userId]);

  React.useEffect(() => {
    if (!open || !isInvestor) return;
    void loadInvestorInstitutionContext();
  }, [open, isInvestor, loadInvestorInstitutionContext]);

  React.useEffect(() => {
    if (!open || termsContentHash) return;
    agreementService.getAgreementPdfUrl('terms')
      .then((res) => {
        setTermsContentHash(res.content_hash || '');
        setTermsVersion(res.agreement_version || '1.0');
      })
      .catch((err) => {
        console.error('Error fetching terms PDF metadata:', err);
      });
  }, [open, termsContentHash]);

  const handleOpenTermsModal = () => {
    setShowTermsViewModal(true);
  };

  const handleOpenRevShareViewModal = () => {
    setShowRevShareViewModal(true);
  };

  const persistAgreementsOnly = async (agreementDataToSubmit) => {
    const dataWithUserId = {
      user_id: user.user_id || user.userId,
      ...agreementDataToSubmit,
    };
    await authService.acceptUserAgreements(dataWithUserId);
    updateUserAgreementStatus(dataWithUserId);
  };

  const submitAgreements = async (agreementDataToSubmit) => {
    setIsSubmitting(true);
    setBillingCompleting(true);

    try {
      await persistAgreementsOnly(agreementDataToSubmit);
      await refreshUserData();
      onComplete();
    } catch (error) {
      console.error('Error submitting agreements:', error);
      setBillingCompleting(false);
      setSnackbar({
        open: true,
        message: 'Failed to submit agreement. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load job titles for investor declaration (legacy helper — prefer handleOpenAccreditedInvestorDeclaration)
  const handleInvestorTermsCheck = (event) => {
    setTermsAccepted(event.target.checked);
  };

  const openDeclarationAfterQualificationsCheck = useCallback(
    async (categories) => {
      const titles = await lookupService.getJobTitles();
      setJobTitles(titles || []);
      if (!categories.length) {
        setShowQualificationsModal(true);
        return;
      }
      setShowAccreditedModal(true);
    },
    [],
  );

  const handleOpenAccreditedInvestorDeclaration = async () => {
    try {
      setLoadingJobTitles(true);
      const categories = await loadInvestorInstitutionContext();
      await openDeclarationAfterQualificationsCheck(categories);
    } catch (loadErr) {
      console.error('Error loading accredited declaration data:', loadErr);
    } finally {
      setLoadingJobTitles(false);
    }
  };

  const handleQualificationsDone = async (values) => {
    const institutionId = user?.institution_id || user?.institutionId || institutionEntityData?.institution_id;
    if (!institutionId) {
      setSnackbar({
        open: true,
        message: 'Institution profile not found. Please contact support.',
        severity: 'error',
      });
      return;
    }
    setLoadingJobTitles(true);
    try {
      const mergedForm = {
        ...mapInvestorSeedFromApiUser(user),
        ...mapInvestorSeedFromApiUser(institutionEntityData),
        qualificationCategories: values.qualificationCategories,
      };
      await entityService.updateEntity(
        'institution',
        institutionId,
        mapInvestorFormToEntityUpdate(mergedForm),
      );
      setInstitutionQualificationCategories(values.qualificationCategories);
      setShowQualificationsModal(false);
      await openDeclarationAfterQualificationsCheck(values.qualificationCategories);
    } catch (saveErr) {
      console.error('Error saving qualification categories:', saveErr);
      setSnackbar({
        open: true,
        message: saveErr.message || 'Could not save qualification categories. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoadingJobTitles(false);
    }
  };

  const handleQualificationsCancel = () => {
    setShowQualificationsModal(false);
  };

  // Handle Revenue Share signature submission (managers only)
  const handleRevShareSign = async (signatureData) => {
    const { reportSubmitStatus, ...sig } = signatureData;
    try {
      if (agreementData && Object.keys(agreementData).length > 0) {
        reportSubmitStatus?.('Saving your platform acceptance…');
        await persistAgreementsOnly(agreementData);
      }

      reportSubmitStatus?.('Recording your signature and applying it to your agreement…');
      await selectRevShareBilling({
        firmId: user?.firm?.firm_id || user?.firm_id || user?.manager_firm_id,
        userId: user?.user_id || user?.userId,
        signatureData: sig,
      });

      setBillingCompleting(true);
      setShowRevShareModal(false);
      await refreshUserData();
      onComplete();
    } catch (error) {
      console.error('❌ Error processing revenue share signature:', error);
      setBillingCompleting(false);
      setSnackbar({
        open: true,
        message: 'Failed to submit signature. Please try again.',
        severity: 'error'
      });
      throw error;
    }
  };

  const handleRevShareCancel = () => {
    setShowRevShareModal(false);
  };

  // Handle Accredited Investor Declaration signature submission (investors only)
  const handleAccreditedInvestorSign = async (signatureData) => {
    try {
      const contentHash = signatureData.agreementContentHash || termsContentHash;

      const fullAgreementData = {
        accredited_investor_declaration: {
          accepted: true,
          attestation_entity_name: signatureData.attestationEntityName,
          signature_name: signatureData.fullName,
          signature_title: signatureData.title,
          signature_title_display: signatureData.titleDisplay,
          signature_date: signatureData.timestamp,
          signature_ip: signatureData.ipAddress,
          agreement_version: signatureData.agreementVersion || termsVersion,
          content_hash: signatureData.agreementContentHash || contentHash,
          draft_s3_key: signatureData.draftS3Key,
          qualification_categories: signatureData.qualificationCategories || [],
          qualified_purchaser: (signatureData.qualificationCategories || []).includes(
            'qualified_purchaser'
          ),
          user_agent: signatureData.userAgent,
          signed_at: signatureData.timestamp
        }
      };

      if (!hasAcceptedTerms && termsAccepted) {
        fullAgreementData.agreement_accepted = true;
        fullAgreementData.agreement_accepted_at = signatureData.timestamp;
        fullAgreementData.agreement_version = termsVersion;
      }

      setShowAccreditedModal(false);
      await submitAgreements(fullAgreementData);
    } catch (error) {
      console.error('Error processing accredited investor declaration:', error);
      setSnackbar({
        open: true,
        message: 'Failed to submit declaration. Please try again.',
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  const handleAccreditedInvestorCancel = () => {
    setShowAccreditedModal(false);
  };

  const handleBillingSelectionRevShare = (agreementDataFromBilling) => {
    if (agreementDataFromBilling) {
      setAgreementData(agreementDataFromBilling);
    }
    setShowRevShareModal(true);
  };

  const handleBillingSelectionDefer = async (agreementDataFromBilling) => {
    const termsToSubmit = agreementDataFromBilling || agreementData;
    try {
      setIsSubmitting(true);

      const requestData = {
        firm_id: user?.firm?.firm_id || user?.firm_id || user?.manager_firm_id,
        user_id: user?.user_id || user?.userId,
        billing_model: 'deferred'
      };

      const response = await billingService.selectBillingModel(requestData);

      if (termsToSubmit && Object.keys(termsToSubmit).length > 0) {
        await submitAgreements(termsToSubmit);
        return;
      }

      const deferredUntil = response?.firm_deferred_until || response?.deferred_until;
      let daysMessage = '30 days';
      let expiryDate = '';
      
      if (deferredUntil) {
        const expiry = new Date(deferredUntil);
        const now = new Date();
        const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
        if (diffDays > 0) {
          daysMessage = `${diffDays} days`;
          expiryDate = expiry.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }
      } else {
        // First time defer - calculate 30 days from now
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 30);
        expiryDate = futureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
      
      // Show success notification
      setSnackbar({
        open: true,
        message: `Trial period: ${daysMessage} remaining (expires ${expiryDate}). Opportunities will not be published to allocators until a participation model is selected.`,
        severity: 'success'
      });

      setBillingCompleting(true);
      await refreshUserData();
      onComplete();
    } catch (error) {
      console.error('❌ Error deferring billing:', error);
      setBillingCompleting(false);
      setSnackbar({
        open: true,
        message: 'Failed to defer participation decision. Please try again.',
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  const _handleBillingSubscription = (plan, agreementDataFromBilling) => {
    setPendingSubscriptionPlan(plan || 'annual');
    setPendingSubscriptionBillingAgreement(agreementDataFromBilling || agreementData);
    setShowSubscriptionSignModal(true);
  };

  const handleSubscriptionAgreementSigned = async (signatureData) => {
    const { reportSubmitStatus, ...sig } = signatureData;
    try {
      const termsToSubmit = pendingSubscriptionBillingAgreement || agreementData;
      if (termsToSubmit && Object.keys(termsToSubmit).length > 0) {
        reportSubmitStatus?.('Saving your platform acceptance…');
        await persistAgreementsOnly(termsToSubmit);
      }

      reportSubmitStatus?.('Recording your signature…');
      setShowSubscriptionSignModal(false);
      setStripeRedirecting(true);

      await recordSubscriptionAndCheckout({
        firmId: user?.firm?.firm_id || user?.firm_id || user?.manager_firm_id,
        userId: user?.user_id || user?.userId,
        signatureData: sig,
        plan: pendingSubscriptionPlan,
      });
    } catch (error) {
      console.error('❌ Error starting subscription checkout:', error);
      setStripeRedirecting(false);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.message || 'Unable to start checkout. Please try again.',
        severity: 'error'
      });
      throw error;
    }
  };

  const handleBillingCancel = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('❌ Error logging out:', error);
    }
  };

  /** Rev-share firm already finalized (e.g. admin offline agreement); submit platform T&C only. */
  const handleManagerRevShareTcOnlyContinue = async () => {
    if (!termsAccepted) return;
    const acceptedAt = new Date().toISOString();
    await submitAgreements({
      agreement_accepted: true,
      agreement_accepted_at: acceptedAt,
      agreement_version: termsVersion,
    });
  };

  /** Legacy investor: declaration already on file; only platform T&C remains. */
  const handleInvestorTcOnlyContinue = async () => {
    if (!termsAccepted) return;
    const acceptedAt = new Date().toISOString();
    await submitAgreements({
      agreement_accepted: true,
      agreement_accepted_at: acceptedAt,
      agreement_version: termsVersion,
    });
  };

  if (!open || !user || userRole === 'admin') return null;

  return (
    <>
      {/* Managers with firm rev-share already on file (e.g. admin invite): platform T&C only */}
      {isManager && managerTcOnly && (
        <Dialog
          open={billingPickerVisible && managerTcOnly}
          disableEscapeKeyDown
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : theme.shape.borderRadius,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              py: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
              px: { xs: theme.spacing(2), sm: theme.spacing(3) },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <GavelIcon
                sx={{
                  mr: theme.spacing(1.5),
                  color: theme.palette.primary.main,
                  fontSize: { xs: '1.5rem', sm: '1.75rem' },
                }}
              />
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: FONT_WEIGHT.medium,
                    color: theme.palette.text.primary,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  Terms and Conditions
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                  One-time acceptance to continue
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ py: theme.spacing(3), px: { xs: theme.spacing(2), sm: theme.spacing(3) } }}>
            <Alert
              severity="info"
              sx={{
                mb: theme.spacing(2),
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              }}
            >
              <Typography variant="body2">
                Your revenue-share arrangement is already on file with HFRConnect. Please review and accept the
                platform Terms & Conditions to finish signing in.
              </Typography>
            </Alert>

            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={handleInvestorTermsCheck}
                  color="primary"
                  disabled={isSubmitting}
                  sx={{ minWidth: TOUCH.minSize, minHeight: TOUCH.minSize }}
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleOpenTermsModal}
                    disabled={isSubmitting}
                    sx={{
                      p: 0,
                      minWidth: 'auto',
                      textDecoration: 'underline',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Terms and Conditions
                  </Button>
                </Typography>
              }
            />
            {!termsAccepted && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: theme.spacing(1), ml: theme.spacing(4) }}>
                You must accept the Terms and Conditions to continue.
              </Typography>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              px: { xs: theme.spacing(2), sm: theme.spacing(3) },
              py: theme.spacing(2),
              borderTop: `1px solid ${theme.palette.divider}`,
              gap: theme.spacing(1),
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <Button variant="outlined" onClick={handleBillingCancel} disabled={isSubmitting} sx={{ minHeight: TOUCH.minSize }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleManagerRevShareTcOnlyContinue}
              disabled={isSubmitting || !termsAccepted}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ minHeight: TOUCH.minSize }}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Managers: billing picker retained for legacy requires_billing_selection (subscription incomplete). */}
      {isManager && !managerTcOnly && (
        <PostLoginBillingModal
          open={billingPickerVisible && !managerTcOnly}
          deferredUntil={user?.firm_deferred_until}
          canDefer={false}
          onSelectRevShare={handleBillingSelectionRevShare}
          onSelectDefer={handleBillingSelectionDefer}
          onCancel={handleBillingCancel}
          showTermsCheckbox={!hasAcceptedTerms}
          onOpenTermsView={handleOpenTermsModal}
          onOpenRevShareView={handleOpenRevShareViewModal}
        />
      )}

      {/* Investors: T&C only when declaration already accepted (legacy / post-onboarding gap) */}
      {isInvestor && investorTcOnly && (
        <Dialog
          open={billingPickerVisible && investorTcOnly}
          disableEscapeKeyDown
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : theme.shape.borderRadius,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              py: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
              px: { xs: theme.spacing(2), sm: theme.spacing(3) },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <GavelIcon
                sx={{
                  mr: theme.spacing(1.5),
                  color: theme.palette.primary.main,
                  fontSize: { xs: '1.5rem', sm: '1.75rem' },
                }}
              />
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: FONT_WEIGHT.medium,
                    color: theme.palette.text.primary,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  Terms and Conditions
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                  One-time acceptance to continue
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ py: theme.spacing(3), px: { xs: theme.spacing(2), sm: theme.spacing(3) } }}>
            <Alert
              severity="info"
              sx={{
                mb: theme.spacing(2),
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              }}
            >
              <Typography variant="body2">
                Your accredited investor declaration is already on file. Please review and accept the
                platform Terms and Conditions to finish signing in.
              </Typography>
            </Alert>

            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={handleInvestorTermsCheck}
                  color="primary"
                  disabled={isSubmitting}
                  sx={{ minWidth: TOUCH.minSize, minHeight: TOUCH.minSize }}
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleOpenTermsModal}
                    disabled={isSubmitting}
                    sx={{
                      p: 0,
                      minWidth: 'auto',
                      textDecoration: 'underline',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Terms and Conditions
                  </Button>
                </Typography>
              }
            />
            {!termsAccepted && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: 'block', mt: theme.spacing(1), ml: theme.spacing(4) }}
              >
                You must accept the Terms and Conditions to continue.
              </Typography>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              px: { xs: theme.spacing(2), sm: theme.spacing(3) },
              py: theme.spacing(2),
              borderTop: `1px solid ${theme.palette.divider}`,
              gap: theme.spacing(1),
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleBillingCancel}
              disabled={isSubmitting}
              sx={{ minHeight: TOUCH.minSize }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleInvestorTcOnlyContinue}
              disabled={isSubmitting || !termsAccepted}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ minHeight: TOUCH.minSize }}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Investors: T&C + declaration when declaration not yet accepted */}
      {isInvestor && investorNeedsDeclaration && (
        <Dialog
          open={billingPickerVisible}
          disableEscapeKeyDown
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : theme.shape.borderRadius,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`
            }
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              py: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
              px: { xs: theme.spacing(2), sm: theme.spacing(3) }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <GavelIcon
                sx={{
                  mr: theme.spacing(1.5),
                  color: theme.palette.primary.main,
                  fontSize: { xs: '1.5rem', sm: '1.75rem' }
                }}
              />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Action Required
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                  One-time acceptance needed
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ py: theme.spacing(3), px: { xs: theme.spacing(2), sm: theme.spacing(3) } }}>
            <Alert
              severity="info"
              sx={{
                mb: theme.spacing(3),
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
              }}
            >
              <Typography variant="body2">
                To continue accessing HFRConnect, you must accept our Terms and Conditions and
                complete the Accredited Investor Declaration.
              </Typography>
            </Alert>

            {!hasAcceptedTerms && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={handleInvestorTermsCheck}
                      color="primary"
                      disabled={isSubmitting}
                      sx={{ minWidth: TOUCH.minSize, minHeight: TOUCH.minSize }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I agree to the{' '}
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleOpenTermsModal}
                        disabled={isSubmitting}
                        sx={{ p: 0, minWidth: 'auto', textDecoration: 'underline', '&:hover': { textDecoration: 'underline' } }}
                      >
                        Terms and Conditions
                      </Button>
                    </Typography>
                  }
                />

                {!termsAccepted && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: theme.spacing(1), ml: theme.spacing(4) }}>
                    You must accept the Terms and Conditions to continue.
                  </Typography>
                )}
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: theme.spacing(3) }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleOpenAccreditedInvestorDeclaration}
                disabled={isSubmitting || loadingJobTitles || (!hasAcceptedTerms && !termsAccepted)}
                startIcon={<GavelIcon />}
                sx={{
                  minWidth: { xs: '100%', sm: 300 },
                  minHeight: TOUCH.comfySize,
                  py: theme.spacing(1.5)
                }}
              >
                {loadingJobTitles ? 'Loading...' : 'Sign Accredited Investor Declaration'}
              </Button>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: { xs: theme.spacing(2), sm: theme.spacing(3) }, py: theme.spacing(2), borderTop: `1px solid ${theme.palette.divider}`, justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Accept the Terms and Conditions above, then sign the declaration.
            </Typography>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={showQualificationsModal}
        onClose={handleQualificationsCancel}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : theme.shape.borderRadius,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            py: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
            px: { xs: theme.spacing(2), sm: theme.spacing(3) },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: FONT_WEIGHT.medium }}>
            Accredited investor qualifications
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
            Required before your personalized declaration can be generated
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ py: theme.spacing(3), px: { xs: theme.spacing(2), sm: theme.spacing(3) } }}>
          <Formik
            initialValues={{
              qualificationCategories: institutionQualificationCategories,
            }}
            enableReinitialize
            validationSchema={qualificationsValidationSchema}
            onSubmit={handleQualificationsDone}
          >
            {({ values, errors, setFieldValue, submitForm, isSubmitting, submitCount }) => (
              <Box>
                <QualificationCategoriesStep
                  values={values}
                  errors={errors}
                  setFieldValue={setFieldValue}
                  stepFlagged={submitCount > 0}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    mt: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleQualificationsCancel}
                    disabled={loadingJobTitles || isSubmitting}
                    sx={{ minHeight: TOUCH.minSize }}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={submitForm}
                    disabled={loadingJobTitles || isSubmitting}
                    sx={{ minHeight: TOUCH.minSize }}
                  >
                    Continue to declaration
                  </Button>
                </Box>
              </Box>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      <ViewTermsModal
        open={showTermsViewModal}
        onClose={() => setShowTermsViewModal(false)}
        type="terms"
        authMode="authenticated"
      />

      <ViewRevShareModal
        open={showRevShareViewModal}
        onClose={() => setShowRevShareViewModal(false)}
      />

      <ESignatureModal
        open={showRevShareModal}
        onClose={handleRevShareCancel}
        onSign={handleRevShareSign}
        isLoading={false}
        firmData={firmData}
        userData={userData}
        agreementType="revenue_share"
      />

      <ESignatureModal
        open={showSubscriptionSignModal}
        onClose={() => setShowSubscriptionSignModal(false)}
        onSign={handleSubscriptionAgreementSigned}
        isLoading={false}
        firmData={firmData}
        userData={userData}
        agreementType="subscription"
      />

      <AccreditedInvestorDeclarationModal
        open={showAccreditedModal}
        onClose={handleAccreditedInvestorCancel}
        onSign={handleAccreditedInvestorSign}
        entityName={user?.institution_name || user?.institutionName || user?.entityName || ''}
        institutionId={user?.institution_id || user?.institutionId || null}
        jobTitles={jobTitles}
        isLoading={loadingJobTitles}
        termsUrl={null}
        termsAlreadyAccepted={true}
        preSelectedTitle={user?.investor_contact_title || user?.contactTitle || null}
        qualificationCategories={institutionQualificationCategories}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ maxWidth: { xs: '100%', sm: 600 } }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', minWidth: { xs: 280, sm: 400 }, maxWidth: 600, borderRadius: theme.shape.borderRadius }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {(stripeRedirecting || billingCompleting) && (
        <Backdrop
          open
          sx={{
            zIndex: ZINDEX.modal + 1,
            flexDirection: 'column',
            gap: 2,
            color: 'common.white',
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="body2" color="inherit">
            {stripeRedirecting ? 'Redirecting to Stripe…' : 'Finishing setup…'}
          </Typography>
        </Backdrop>
      )}
    </>
  );
};

PostLoginAgreementModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onComplete: PropTypes.func.isRequired
};

export default PostLoginAgreementModal;

